import type { Command } from '@atlaskit/editor-common/types/command';
import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types/next-editor-plugin';
import type { Node as PMNode } from '@atlaskit/editor-prosemirror/model';
import type { NativeEmbedParameters } from '@atlaskit/native-embeds-common/types';
import { setParameters } from '@atlaskit/native-embeds-common/utils';

import type { EditorPluginNativeEmbedsPlugin } from '../../nativeEmbedsPluginType';

/** Default border color matching the image border default. */
export const DEFAULT_NATIVE_EMBED_BORDER_COLOR = '#091E4224';
/** Default border size matching the image border default (Subtle = 1). */
export const DEFAULT_NATIVE_EMBED_BORDER_SIZE = 1;

export type NativeEmbedBorderAttrs = {
	color?: string;
	size?: number;
};

export const getBorderAttrs = (selectedNode: PMNode): NativeEmbedBorderAttrs => {
	const macroParams = (selectedNode.attrs.parameters as NativeEmbedParameters)?.macroParams ?? {};
	// Access as extended params (borderColor/borderSize added in MAUI-478)
	const extendedParams = macroParams as NativeEmbedParameters['macroParams'] & {
		borderColor?: { value: string };
		borderSize?: { value: string };
	};
	return {
		color: extendedParams.borderColor?.value || undefined,
		size: extendedParams.borderSize?.value ? Number(extendedParams.borderSize.value) : undefined,
	};
};

/**
 * Removes borderColor and borderSize keys from macroParams entirely.
 */
const removeBorderFromParameters = (
	parameters: NativeEmbedParameters | undefined,
): NativeEmbedParameters => {
	// Cast to access borderColor/borderSize which are MAUI-478 additions not yet in compiled types
	type ExtendedMacroParams = NativeEmbedParameters['macroParams'] & {
		borderColor?: { value: string };
		borderSize?: { value: string };
	};
	const {
		borderColor: _bc,
		borderSize: _bs,
		...restMacroParams
	} = (parameters?.macroParams ?? {}) as ExtendedMacroParams;
	return {
		...(parameters ?? {}),
		macroParams: restMacroParams as NativeEmbedParameters['macroParams'],
	};
};

/**
 * Sets the border color for a native embed node.
 * If no border size is set yet, also sets the default size.
 */
export const createSetBorderColorCommand =
	(
		api: ExtractInjectionAPI<EditorPluginNativeEmbedsPlugin> | undefined,
		color: string,
		selectedNode: PMNode,
	): Command =>
	() => {
		const currentParams = selectedNode.attrs.parameters as NativeEmbedParameters | undefined;
		const extendedParams = currentParams?.macroParams as
			| (NativeEmbedParameters['macroParams'] & {
					borderSize?: { value: string };
			  })
			| undefined;
		const hasBorderSize = !!extendedParams?.borderSize?.value;
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		const update: any = {
			borderColor: color,
			// Only set a default size if none is already stored
			...(!hasBorderSize ? { borderSize: DEFAULT_NATIVE_EMBED_BORDER_SIZE } : {}),
		};
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		const newParameters = setParameters(currentParams, update as any);
		api?.extension?.actions?.api()?.doc?.update(selectedNode.attrs.localId, (current) => ({
			attrs: { ...current.attrs, parameters: newParameters },
		}));
		return true;
	};

/**
 * Sets the border size for a native embed node.
 */
export const createSetBorderSizeCommand =
	(
		api: ExtractInjectionAPI<EditorPluginNativeEmbedsPlugin> | undefined,
		size: number,
		selectedNode: PMNode,
	): Command =>
	() => {
		const currentParams = selectedNode.attrs.parameters as NativeEmbedParameters | undefined;
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		const newParameters = setParameters(currentParams, { borderSize: size } as any);
		api?.extension?.actions?.api()?.doc?.update(selectedNode.attrs.localId, (current) => ({
			attrs: { ...current.attrs, parameters: newParameters },
		}));
		return true;
	};

/**
 * Toggles the border on/off for a native embed node.
 * - If the border is currently on (borderColor is set and non-empty), removes borderColor and borderSize.
 * - If the border is currently off, sets default color and size.
 */
export const createToggleBorderCommand =
	(
		api: ExtractInjectionAPI<EditorPluginNativeEmbedsPlugin> | undefined,
		selectedNode: PMNode,
	): Command =>
	() => {
		const currentParams = selectedNode.attrs.parameters as NativeEmbedParameters | undefined;
		type ExtendedMacroParams = NativeEmbedParameters['macroParams'] & {
			borderColor?: { value: string };
		};
		const currentColor = (currentParams?.macroParams as ExtendedMacroParams | undefined)
			?.borderColor?.value;
		let newParameters: NativeEmbedParameters;
		if (currentColor) {
			// Remove border — delete borderColor and borderSize from macroParams
			newParameters = removeBorderFromParameters(currentParams);
		} else {
			// Add border with defaults
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			newParameters = setParameters(currentParams, {
				borderColor: DEFAULT_NATIVE_EMBED_BORDER_COLOR,
				borderSize: DEFAULT_NATIVE_EMBED_BORDER_SIZE,
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
			} as any);
		}
		api?.extension?.actions?.api()?.doc?.update(selectedNode.attrs.localId, (current) => ({
			attrs: { ...current.attrs, parameters: newParameters },
		}));
		return true;
	};
